import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import {
  FREE_CHARS_PER_TRANSLATION,
  GUEST_CHARS_PER_TRANSLATION,
  PRO_CHARS_PER_TRANSLATION,
} from '@/lib/baba-limits'
import {
  translateStream,
  generateSpeech,
  fetchTranslationVariations,
  LANGUAGES,
  toLangCode,
  type LangCode,
  type GenderId,
  type ModeId,
  type ModelId,
  type UsageSnapshot,
  type VoiceGender,
} from '@/lib/baba-api'
import { isSignedIn } from '@/lib/baba-auth'
import { trackFirstTranslation } from '@/lib/analytics'
import { buildTextTranslationSyncRow } from '@/lib/library/gatewayLibraryMapping'
import { syncOneGatewayTranslationRow } from '@/lib/library/gatewayLibraryClient'
import {
  ensureGatewaySourceProbe,
  favoriteGatewayTranslation,
  gatewaySourceStatus,
  noteGatewayTranslationRow,
  unfavoriteGatewayTranslation,
} from '@/lib/library/useLibrarySource'
import { markAppBusy } from '@/lib/appUpdateCheck'
import { useProgress } from '../hooks/useProgress'
import * as offlineVoice from '@/lib/pocketTts'
import { babaApi, useSubscriptionStatusQuery } from '../store/babaApi'
import { useAppDispatch } from '../store/hooks'
import {
  DEFAULT_SOURCE,
  DEFAULT_TARGET,
  RECENT_TRANSLATIONS_KEY,
  HANDOFF_AUTOTRANSLATE_KEY,
} from './translator/constants'
import type { AppPrefs, RecentTranslation, RestoredMedia, TranslatorTab } from './translator/types'
import {
  detectScript,
  fileToBase64,
  loadDraftText,
  loadPrefs,
  loadRecentTranslations,
  persistDraftText,
  savePrefs,
} from './translator/utils'
import {
  FILE_MAX_BYTES,
  FILE_MAX_MB,
  detectFileKind,
  officeFileType,
  type TranslatorFileKind,
} from './translator/fileKind'
import { mediaResultCopyText, parseMediaResult, type MediaResult } from '@/lib/library/mediaResultContent'
import {
  applyTranslationStreamError,
  type TranslationStreamError,
} from './translator/translationStreamError'
import {
  runTextTranslation,
  runPdfTranslation,
  runImageTranslation,
  runDocTranslation,
} from './translator/runTranslateStreams'
import { prefetchViaGateway } from '@/lib/translateGateway/client'
import {
  classifyTextChange,
  normalizeTranslateText,
  prefetchSignature,
  shouldSkipPrefetch,
  PREFETCH_PAUSE_DELAY_MS,
} from './translator/prefetchTrigger'
import { MobileUsageStrip } from './translator/subcomponents'
import { Toolbar } from './translator/Toolbar'
import { HistoryPanel } from './translator/HistoryPanel'
import type { HistoryRow, HistoryRowMedia } from '@/lib/library/historyPanelRows'
import { StatusBar } from './translator/StatusBar'
import { CommandPalette } from './translator/CommandPalette'
import { cn } from '@/lib/utils'
import { MobileHeader, MobileBottomBar, MobileHistorySheet, MobilePageHeader } from './translator/MobileChrome'
import { TranslatorSourcePane } from './translator/TranslatorSourcePane'
import { TranslatorTargetPane } from './translator/TranslatorTargetPane'
import { WordAlternativesPopover } from './translator/WordAlternativesPopover'
import {
  advanceFileJob,
  createFileJob,
  type FileJobEvent,
  type FileJobState,
} from './translator/fileTranslationProgress'
import { usePaywall } from './paywall'
import type { LimitCardKind } from './translator/planDisplay'
import { useSignIn } from './SignInModal'
import ContextPanel from './ContextPanel'

const OUTPUT_KEY = 'baba.app.output.v1'
const REVERSE_INTRO_KEY = 'baba.app.reverse_intro_seen'
const HEBREW_FACTS = [
  'Hebrew is one of the oldest languages still spoken today.',
  'Modern Hebrew was revived in the 19th century by Eliezer Ben-Yehuda.',
  'Hebrew reads right to left.',
  'The Hebrew alphabet has 22 letters, all consonants.',
  'baba is available on iPhone and Android.',
  'Over 9 million people speak Hebrew worldwide.',
  'Hebrew has no capital letters.',
  'The word "Shalom" means peace, hello, and goodbye.',
]

export default function Translator() {
  const { open: openPaywall } = usePaywall()
  const { open: openSignIn } = useSignIn()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { recordTranslation } = useProgress()

  const [tab, setTab] = useState<TranslatorTab>('text')

  const [sourceLang, setSourceLang] = useState<LangCode>(DEFAULT_SOURCE)
  const [targetLang, setTargetLang] = useState<LangCode>(DEFAULT_TARGET)
  const [inputText, setInputText] = useState(() => loadDraftText())
  const [output, setOutput] = useState('')
  const outputRef = useRef(output)
  outputRef.current = output
  // File tabs (Documents, Image) always return BOTH a full translation and a
  // key-points summary. `summary` holds the bullets that render in the summary
  // bar beneath the panes; `originalText` holds the image's OCR'd Hebrew that
  // renders in the original-language pane. Both are empty on the text tab.
  const [summary, setSummary] = useState('')
  const [originalText, setOriginalText] = useState('')
  // A finished image/PDF translation is a structured document, not prose (see
  // runTranslateStreams' applyGatewayMediaResult). When this is set the target
  // pane renders it through the shared media view instead of the markdown pane.
  const [mediaResult, setMediaResult] = useState<MediaResult | null>(null)
  const [restoredMedia, setRestoredMedia] = useState<RestoredMedia | null>(null)
  const [recentTranslations, setRecentTranslations] = useState<RecentTranslation[]>(() => loadRecentTranslations())
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false)
  const [activeRecentId, setActiveRecentId] = useState<string | null>(null)
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false)
  const [reverseOutput, setReverseOutput] = useState('')
  const [isReversing, setIsReversing] = useState(false)
  const [showCopyMenu, setShowCopyMenu] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const lastOutputRef = useRef<string>('')
  const lastTranslatedInputRef = useRef<string>('')
  // Id of the last text translation best-effort-pushed to the shared gateway
  // translations ledger (see runTranslate's gateway push below), so a
  // same-turn favorite upserts the SAME row instead of creating a second one.
  const gatewayLastPushRef = useRef<{ id: string; translatedText: string } | null>(null)
  const [transliteration, setTransliteration] = useState('')
  const [gender, setGender] = useState<GenderId>('general')
  const [speakingTo, setSpeakingTo] = useState<GenderId>('general')
  const [mode, setMode] = useState<ModeId>('standard')
  // Everyone gets the smartest model. No picker, no gate.
  const model: ModelId = 'balanced'
  const style = ''
  const [showGenderNudge, setShowGenderNudge] = useState(false)
  const [isTranslating, setIsTranslating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Which free limit refused the last text. Drives the inline card in the result area.
  const [limitKind, setLimitKind] = useState<LimitCardKind | null>(null)
  const limitReached = limitKind === 'monthly'
  const setLimitReached = useCallback((v: boolean) => setLimitKind(v ? 'monthly' : null), [])
  const [copied, setCopied] = useState(false)
  const [savedOutput, setSavedOutput] = useState('')
  const [usage, setUsage] = useState<UsageSnapshot | null>(null)
  const [isPro, setIsPro] = useState(false)
  const [isProLoaded, setIsProLoaded] = useState(false)
  const [isAuthed, setIsAuthed] = useState(false)
  const { data: subscription } = useSubscriptionStatusQuery(undefined, { skip: !isAuthed })
  const [wordPopover, setWordPopover] = useState<{
    word: string
    x: number
    y: number
    alternatives: string[]
    loading: boolean
  } | null>(null)
  const [variations, setVariations] = useState<string[]>([])
  const [variationsLoading, setVariationsLoading] = useState(false)

  // One picked file for the whole File tab. Its kind decides the backend, the
  // quota and the paywall reason (see ./translator/fileKind.ts) — the person
  // uploading it does not have to.
  const [file, setFile] = useState<File | null>(null)
  const [fileKind, setFileKind] = useState<TranslatorFileKind | null>(null)
  /** Raw base64, no data-URL prefix. The PDF and Office routes want it this
   *  way; the image route wants the full data URL and rebuilds it below. */
  const [fileBase64, setFileBase64] = useState<string>('')
  const [pdfPageCount, setPdfPageCount] = useState<number | undefined>(undefined)

  const [voiceGender, setVoiceGender] = useState<VoiceGender>('female')
  const [isSpeaking, setIsSpeaking] = useState(false)
  /** Between the click and the first sound: generating, fetching, decoding.
   *  ElevenLabs takes a beat, and a button that does nothing visible for two
   *  seconds reads as a broken button. */
  const [isPreparingSpeech, setIsPreparingSpeech] = useState(false)
  const [autoCopy, setAutoCopy] = useState(false)
  const [showReverseIntro, setShowReverseIntro] = useState(false)
  const [translationStartMs, setTranslationStartMs] = useState<number | null>(null)
  const [translationElapsedMs, setTranslationElapsedMs] = useState(0)
  const [factIndex, setFactIndex] = useState(0)
  const [isSummarizing, setIsSummarizing] = useState(false)
  const [fileJob, setFileJob] = useState<FileJobState | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const audioUrlRef = useRef<string | null>(null)
  const pendingSpeakingToRef = useRef<GenderId | null>(null)
  const pendingAudienceChangeFromIdRef = useRef<string | null>(null)
  const pendingAudienceChangeTokenRef = useRef<string | null>(null)
  const apiLastAudienceResultRef = useRef<{ token: string; translatedText: string } | null>(null)
  const runTranslateRef = useRef<() => void>(() => {})
  // Bumped whenever the pane is cleared, so a reverse check still streaming
  // for the previous result cannot write itself under the next one.
  const reverseRunRef = useRef(0)
  const suppressAutoTranslateRef = useRef(false)
  const prefsRef = useRef<Partial<AppPrefs>>({})

  // Pre-translation prefetch (warms the gateway's cache before the user
  // hits Translate — see `prefetchViaGateway` and `./translator/prefetchTrigger.ts`).
  // `prefetchPrevTextRef` seeds from the restored draft so a fresh mount
  // never reads as one big paste; `justPastedRef` is set by the paste
  // listener below and by `handlePasteFromClipboard`, then consumed (and
  // cleared) by the effect that drives this. `userEditRef` is the composing
  // guard: only a genuine keystroke (`handleInputTextChange`) or paste sets
  // it, so opening a recent item, swapping languages, clearing, or any other
  // programmatic `setInputText` can never trigger a prefetch — see the
  // effect itself for why it still tracks `prefetchPrevTextRef` regardless.
  const prefetchPrevTextRef = useRef(inputText)
  const justPastedRef = useRef(false)
  const userEditRef = useRef(false)
  const prefetchPauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prefetchLastSigRef = useRef<string | null>(null)

  // Shared with the prefetch effect below so a pre-warm request can never
  // resolve the addressee differently than the real translation that
  // follows it — a peek only; `runTranslate` itself is the one place that
  // consumes (clears) `pendingSpeakingToRef`. Reads `speakingTo` through a
  // ref (kept current below, right after `charLimit`) rather than closing
  // over it directly, so a call queued by the prefetch effect's 800ms pause
  // timer still resolves against whatever the picker says by the time it
  // actually fires, not whatever it said 800ms earlier.
  const speakingToRef = useRef(speakingTo)
  const resolveEffectiveSpeakingTo = useCallback(
    (): GenderId => pendingSpeakingToRef.current ?? speakingToRef.current,
    [],
  )

  const setSourceLangSafe = useCallback((lang: LangCode) => {
    setSourceLang(lang)
    if (lang === 'he' && targetLang === 'he') {
      setTargetLang('en')
      return
    }
    if (lang !== 'he' && targetLang !== 'he') setTargetLang('he')
  }, [targetLang])

  const setTargetLangSafe = useCallback((lang: LangCode) => {
    setTargetLang(lang)
    if (lang === 'he' && sourceLang === 'he') {
      setSourceLang('en')
      return
    }
    if (lang !== 'he' && sourceLang !== 'he') setSourceLang('he')
  }, [sourceLang])

  /**
   * The ONE way a result leaves the target pane. A finished translation is
   * spread over several slots (`output`, `mediaResult`, `summary`,
   * `originalText`, `transliteration`, the reverse check, the alternatives)
   * and every path that puts a new result in — a translate, a pasted file, a
   * History row, a swap — used to clear its own subset. The leftovers stacked:
   * an image card with the previous text result rendered underneath it. Every
   * entry path now goes through here first.
   */
  const clearResult = useCallback(() => {
    reverseRunRef.current += 1
    setOutput('')
    setMediaResult(null)
    setSummary('')
    setOriginalText('')
    setTransliteration('')
    setReverseOutput('')
    setIsReversing(false)
    setVariations([])
    setVariationsLoading(false)
    setWordPopover(null)
    setShowCopyMenu(false)
    setError(null)
    setLimitReached(false)
  }, [])

  const abortRef = useRef<AbortController | null>(null)
  const detectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const copyFlagTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Bumped per Listen press, so a clip that arrives after a cancel is dropped. */
  const speakRunRef = useRef(0)
  /** Settings → Translation → Auto-Copy. The ref is what the finished
   *  translation reads; the state is what the copy menu shows. */
  const autoCopyRef = useRef(false)
  const saveFlagTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // No `!` here. A history row can carry a language this build has no entry
  // for (the shared ledger stores display names such as 'English' as well as
  // codes), and the non-null assertion turned that into a render-time
  // TypeError in the panes below — the "Something went wrong" boundary.
  const sourceMeta = useMemo(() => LANGUAGES.find((l) => l.code === sourceLang), [sourceLang])

  // Someone who picked the on-device voice gets the model loaded now, off the
  // main thread, rather than on their first Listen tap.
  useEffect(() => {
    offlineVoice.warm()
  }, [])

  useEffect(() => {
    if (tab !== 'text') return
    if (detectTimerRef.current) clearTimeout(detectTimerRef.current)
    detectTimerRef.current = setTimeout(() => {
      const detected = detectScript(inputText)
      if (!detected) return
      if (detected === targetLang) {
        setSourceLang(detected)
        setTargetLang(sourceLang === detected ? 'en' : sourceLang)
        return
      }
      if (detected !== sourceLang) {
        setSourceLang(detected)
        if (detected !== 'he' && targetLang !== 'he') setTargetLang('he')
      }
    }, 300)
    return () => { if (detectTimerRef.current) clearTimeout(detectTimerRef.current) }
  }, [inputText, tab, sourceLang, targetLang])

  // Soft limit: text over the cap stays in the input (never truncated — that
  // used to destroy a user's pasted text at the exact moment they'd want to
  // upgrade), the counter turns red, and the Translate button disables. The
  // sign-in/paywall prompt fires only when the user clicks Translate while
  // over the limit — see runTranslate — never mid-paste.
  const charLimit = isPro
    ? PRO_CHARS_PER_TRANSLATION
    : isAuthed
      ? FREE_CHARS_PER_TRANSLATION
      : GUEST_CHARS_PER_TRANSLATION

  // Kept current every render (not through a dependency array) so the
  // prefetch effect below — keyed ONLY on `inputText` so an unrelated
  // picker change can never cancel a keystroke's pending 800ms debounce —
  // still reads live values whenever `sendPrefetch` actually runs, whether
  // that's synchronously (a paste) or after the pause delay.
  speakingToRef.current = speakingTo
  const prefetchLiveRef = useRef({ sourceLang, targetLang, gender, mode, model, style, tab, isTranslating, charLimit })
  prefetchLiveRef.current = { sourceLang, targetLang, gender, mode, model, style, tab, isTranslating, charLimit }

  useEffect(() => {
    const prefs = loadPrefs()
    prefsRef.current = prefs
    if (prefs.sourceLang) setSourceLang(prefs.sourceLang)
    if (prefs.targetLang) setTargetLang(prefs.targetLang)
    if (prefs.gender) setGender(prefs.gender)
    if (prefs.voiceGender) setVoiceGender(prefs.voiceGender)
    if (prefs.speakingTo) setSpeakingTo(prefs.speakingTo)
    autoCopyRef.current = prefs.autoCopy === true
    setAutoCopy(prefs.autoCopy === true)

    const savedOutputVal = localStorage.getItem(OUTPUT_KEY)
    if (savedOutputVal) {
      setOutput(savedOutputVal)
      suppressAutoTranslateRef.current = true
      // Restore the matching transliteration too, so a restored result does
      // not come back missing its pronunciation line.
      try {
        const savedTranslit = localStorage.getItem(`${OUTPUT_KEY}.translit`)
        if (savedTranslit) setTransliteration(savedTranslit)
      } catch {}
    }

    const authed = isSignedIn()
    setIsAuthed(authed)
    if (!authed) {
      setIsProLoaded(true)
    }
  }, [])

  // Landing hand-off (?text=… on app.itsbaba.com/translate): TranslatorPage
  // already wrote the draft + prefs to storage before this component mounted;
  // run the translation once so the visitor sees a result, not an empty box.
  // Same pattern as handleExampleClick — through runTranslate, so quota and
  // sign-in gates apply exactly as they would to a typed request.
  useEffect(() => {
    let flagged = false
    try {
      flagged = sessionStorage.getItem(HANDOFF_AUTOTRANSLATE_KEY) === '1'
      if (flagged) sessionStorage.removeItem(HANDOFF_AUTOTRANSLATE_KEY)
    } catch {}
    if (!flagged) return
    suppressAutoTranslateRef.current = false
    const t = setTimeout(() => { runTranslateRef.current() }, 0)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    if (!isAuthed) return
    if (subscription === undefined) return
    // Token was rejected (expired/invalid) and the fetch already cleared it.
    // Flip back to logged-out so Pro taps open the sign-in modal — NOT the
    // upgrade paywall. Without this, a lapsed paying user keeps isAuthed=true,
    // drops to is_pro=false, and gets asked to pay again for a plan they own.
    if (subscription.authExpired) {
      setIsAuthed(false)
      setIsPro(false)
      setIsProLoaded(true)
      return
    }
    if (subscription.fetchFailed && subscription.status === 'unknown') return
    const pro = subscription.status === 'active' || subscription.is_pro === true
    setIsPro(pro)
    setIsProLoaded(true)
  }, [isAuthed, subscription])

  useEffect(() => {
    const entry = (location.state as { restore?: { source_text: string | null; translated_text: string | null; source_lang: LangCode; target_lang: LangCode } } | null)?.restore
    if (!entry) return
    clearResult()
    setRestoredMedia(null)
    setTab('text')
    // Coerce null/undefined to '' — a media (image/PDF) history entry can
    // carry a null source_text, and every downstream read of inputText/
    // output (inputText.trim(), .length, etc.) assumes a string. Without
    // this, restoring one of those entries threw during render and dropped
    // the whole app to the Sentry error boundary.
    setInputText(entry.source_text ?? '')
    setOutput(entry.translated_text ?? '')
    // Ledger rows carry display names ('English') as often as codes ('en').
    setSourceLang(toLangCode(entry.source_lang, DEFAULT_SOURCE))
    setTargetLang(toLangCode(entry.target_lang, DEFAULT_TARGET))
    window.history.replaceState({}, '')
  }, [location.state, clearResult])

  useEffect(() => {
    savePrefs({ sourceLang, targetLang, gender, voiceGender, speakingTo })
  }, [sourceLang, targetLang, gender, voiceGender, speakingTo])

  useEffect(() => {
    persistDraftText(inputText, tab)
  }, [inputText, tab])

  // Persist text-tab output so navigating to library and back doesn't lose it.
  useEffect(() => {
    if (tab !== 'text') return
    try {
      if (output) {
        localStorage.setItem(OUTPUT_KEY, output)
        if (transliteration) localStorage.setItem(`${OUTPUT_KEY}.translit`, transliteration)
        else localStorage.removeItem(`${OUTPUT_KEY}.translit`)
      } else {
        localStorage.removeItem(OUTPUT_KEY)
        localStorage.removeItem(`${OUTPUT_KEY}.translit`)
      }
    } catch {}
  }, [output, transliteration, tab])

  // Reset savedOutput when the output changes and no longer matches what was saved.
  useEffect(() => {
    if (savedOutput && output.trim() !== savedOutput) setSavedOutput('')
  }, [output, savedOutput])

  // Elapsed time is measured once, when a run ends ("done · 2.4 s"). It used to
  // tick every 50 ms, which re-rendered the whole translator twenty times a
  // second for as long as a translation streamed. The file progress view keeps
  // its own one-second clock from translationStartMs.
  useEffect(() => {
    if (isTranslating || translationStartMs === null) return
    setTranslationElapsedMs(Date.now() - translationStartMs)
  }, [isTranslating, translationStartMs])

  // A translation in flight is work a silent update reload would throw away.
  useEffect(() => {
    if (!isTranslating) return
    return markAppBusy('translation')
  }, [isTranslating])

  // Hebrew facts cycling — cycles every 3s for non-text tabs while translating.
  useEffect(() => {
    if (isTranslating && tab !== 'text') {
      const interval = setInterval(() => {
        setFactIndex((i) => i + 1)
      }, 3000)
      return () => clearInterval(interval)
    }
  }, [isTranslating, tab])

  const targetLanguageName = useMemo(
    () => LANGUAGES.find((l) => l.code === targetLang)?.name || 'English',
    [targetLang],
  )
  /** Thumbnail for the picked file, images only. A data URL rather than an
   *  object URL so there is nothing to revoke when the file is cleared. */
  const filePreviewUrl = useMemo(
    () => (fileKind === 'image' && fileBase64 ? `data:${file?.type || 'image/jpeg'};base64,${fileBase64}` : ''),
    [fileKind, fileBase64, file],
  )
  /** The human sentences of whatever finished, prose or structured document.
   *  Copy, Listen, Share and Save all act on this rather than on `output`, so
   *  they keep working for an image/PDF result and never hand anyone (or a
   *  speech synthesiser) a JSON document. Same rule as the History modal. */
  const resultText = useMemo(
    () => output || (mediaResult ? mediaResultCopyText(mediaResult) : ''),
    [output, mediaResult],
  )
  const hasCompletedTranslation = !!resultText && !isTranslating
  const updateFileStage = useCallback((stage: FileJobEvent['type']) => {
    setFileJob((current) => current ? advanceFileJob(current, { type: stage }) : current)
  }, [])


  /**
   * Auto-copy (Settings → Translation): put a finished translation on the
   * clipboard without a click, so switching back to WhatsApp is a paste.
   * Best-effort by design — a browser that refuses a clipboard write outside
   * a fresh gesture (Safari, Firefox) simply leaves the Copy button to do it,
   * and the user never sees an error for a thing they did not press.
   */
  const maybeAutoCopy = useCallback(async (text: string) => {
    if (!autoCopyRef.current || !text.trim()) return
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return
    // Desktop only, same as the setting that turns it on: a touch browser
    // refuses a clipboard write this far from the tap that started it.
    if (!window.matchMedia('(pointer: fine)').matches) return
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      if (copyFlagTimerRef.current) clearTimeout(copyFlagTimerRef.current)
      copyFlagTimerRef.current = setTimeout(() => setCopied(false), 1400)
    } catch {
      // No clipboard access without a gesture — stay quiet.
    }
  }, [])

  const runTranslate = useCallback(async (retryContext?: string) => {
    if (isTranslating) return

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    const errCtx = {
      isPro,
      isAuthed,
      setError,
      setLimitReached,
      openSignIn,
    }
    const onStreamError = (err: TranslationStreamError) => {
      updateFileStage('error')
      applyTranslationStreamError(err, errCtx)
    }

    const prep = (nextFileJob?: FileJobState) => {
      // A file result is delivered whole on the terminal frame, so nothing
      // would blank a stale `output` in the meantime — clear the pane now.
      // The text stream blanks `output` itself on its first token, which
      // keeps the previous sentence readable while a retranslate is in
      // flight; everything else goes now.
      if (nextFileJob) clearResult()
      // A fresh translation is not the History row that was open before it.
      setActiveRecentId(null)
      setIsTranslating(true)
      setError(null)
      setLimitReached(false)
      setTransliteration('')
      setSummary('')
      setOriginalText('')
      setMediaResult(null)
      reverseRunRef.current += 1
      setReverseOutput('')
      setIsReversing(false)
      setVariations([])
      setWordPopover(null)
      setTranslationStartMs(Date.now())
      setTranslationElapsedMs(0)
      setIsSummarizing(false)
      setFileJob(nextFileJob ?? null)
    }

    if (tab === 'text') {
      // Shared with the prefetch effect below (`normalizeTranslateText`) so
      // a prefetch and the real request it's warming the cache for reduce
      // `inputText` to the identical string the gateway keys its cache on.
      const text = normalizeTranslateText(inputText)
      if (!text) return
      if (!isPro && text.length > charLimit) {
        // Authed free users see the inline card in the result area; the
        // paywall opens from its button.
        setError(null)
        setLimitKind('length')
        if (!isAuthed) openSignIn('upgrade-free')
        return
      }
      prep()
      if (prefetchPauseTimerRef.current) {
        clearTimeout(prefetchPauseTimerRef.current)
        prefetchPauseTimerRef.current = null
      }
      lastTranslatedInputRef.current = inputText
      const effectiveSpeakingTo = resolveEffectiveSpeakingTo()
      const audienceChangeFromId = pendingAudienceChangeFromIdRef.current ?? undefined
      const audienceChangeToken = pendingAudienceChangeTokenRef.current ?? undefined
      pendingSpeakingToRef.current = null
      pendingAudienceChangeFromIdRef.current = null
      pendingAudienceChangeTokenRef.current = null
      const {
        historyEntry,
        historySaveFailed,
        succeeded,
        gatewayHistoryId,
        audienceChangeToken: nextAudienceChangeToken,
      } = await runTextTranslation({
        signal: controller.signal,
        text,
        retryContext,
        sourceLang,
        targetLang,
        gender,
        effectiveSpeakingTo,
        mode,
        model,
        style,
        audienceChangeFromId,
        audienceChangeToken,
        setOutput,
        setTransliteration,
        setUsage,
        setIsTranslating,
        setRecentTranslations,
        setRetryCount,
        lastOutputRef,
        onStreamError,
      })
      setIsTranslating(false)
      if (succeeded) {
        trackFirstTranslation({ signedIn: isAuthed })
        recordTranslation({ chars: text.length + (lastOutputRef.current?.length ?? 0) })
        void maybeAutoCopy(lastOutputRef.current)
        apiLastAudienceResultRef.current = nextAudienceChangeToken
          ? { token: nextAudienceChangeToken, translatedText: lastOutputRef.current.trim() }
          : null
      }
      if (historyEntry) {
        dispatch(babaApi.util.updateQueryData('libraryHistory', undefined, (draft) => {
          const existingIndex = draft.findIndex((entry) => entry.id === historyEntry.id)
          if (existingIndex >= 0) draft.splice(existingIndex, 1)
          draft.unshift(historyEntry)
          draft.sort((a, b) => Date.parse(b.created_at ?? '') - Date.parse(a.created_at ?? ''))
        }))
      }
      if (isAuthed) {
        dispatch(babaApi.util.invalidateTags([{ type: 'Library', id: 'history' }]))
      }
      if (historySaveFailed) toast.error('Translation finished, but history could not be updated.')

      // Best-effort, idempotent mirror onto the shared gateway translations
      // ledger so this translation also shows in the phone's History — never
      // blocks or errors the translation itself. Server history above is the
      // source of truth for the web-only path; this is additive.
      if (succeeded && isAuthed) {
        const translatedText = lastOutputRef.current.trim()
        if (gatewayHistoryId) {
          // This translation already ran on the gateway (one-ledger path —
          // see translateGateway/client.ts) and the gateway already recorded
          // it under this id. Reuse it instead of pushing a duplicate row;
          // the favorite path below reuses the SAME id via gatewayLastPushRef.
          gatewayLastPushRef.current = { id: gatewayHistoryId, translatedText }
          // Shown at the top of History now, rather than by downloading the
          // whole ledger again after every translation.
          noteGatewayTranslationRow(
            buildTextTranslationSyncRow({ id: gatewayHistoryId, sourceText: text, translatedText, sourceLang, targetLang, mode, model, gender }),
          )
        } else {
          const gatewayRowId =
            typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : null
          if (gatewayRowId) {
            const gatewayRow = buildTextTranslationSyncRow({
              id: gatewayRowId,
              sourceText: text,
              translatedText,
              sourceLang,
              targetLang,
              mode,
              model,
              gender,
            })
            syncOneGatewayTranslationRow(gatewayRow)
              .then((ok) => {
                if (ok) {
                  gatewayLastPushRef.current = { id: gatewayRowId, translatedText }
                  noteGatewayTranslationRow(gatewayRow)
                }
              })
              .catch(() => {
                // Gateway unreachable/unauthenticated — the web history path above already succeeded.
              })
          }
        }
      }
      return
    }

    if (tab === 'file') {
      // One tab, three backends, chosen by the file itself: an image and a PDF
      // stream through the gateway (shared ledger, shared monthly allowance),
      // a Word/PowerPoint file through apps/api, which has the only document
      // pipeline. The gates below are unchanged per kind — only the tab that
      // reaches them was merged.
      if (!fileBase64 || !fileKind || !file) return
      if (!isAuthed) {
        openSignIn('sidebar')
        return
      }
      if (!isPro) {
        openPaywall(fileKind === 'image' ? 'image' : 'document')
        return
      }

      prep(createFileJob(fileKind === 'image' ? 'image' : 'document', file.name))
      let fileSucceeded = false
      if (fileKind === 'image') {
        fileSucceeded = await runImageTranslation({
          signal: controller.signal,
          // The gateway's storage upload only matches a full data URL, and a
          // bare payload silently yields a row with no image on the phone —
          // see buildGatewayImageBody in lib/translateGateway/mediaMapping.ts.
          imageBase64: `data:${file.type || 'image/jpeg'};base64,${fileBase64}`,
          targetLanguageName,
          targetLang,
          imageModePick: 'full',
          imageFileName: file.name,
          gender,
          setOutput,
          setMediaResult,
          setSummary,
          setOriginalText,
          setUsage,
          setRecentTranslations,
          onStreamError,
          onStage: updateFileStage,
        })
      } else if (fileKind === 'pdf') {
        fileSucceeded = await runPdfTranslation({
          signal: controller.signal,
          pdfBase64: fileBase64,
          targetLanguageName,
          targetLang,
          pdfMode: 'full',
          pdfFileName: file.name,
          pdfPageCount,
          gender,
          setOutput,
          setMediaResult,
          setSummary,
          setUsage,
          setRecentTranslations,
          onStreamError,
          onStage: updateFileStage,
        })
      } else {
        fileSucceeded = await runDocTranslation({
          signal: controller.signal,
          docBase64: fileBase64,
          docFileType: officeFileType(file),
          docFileName: file.name,
          targetLanguageName,
          targetLang,
          docMode: 'full',
          gender,
          setOutput,
          setSummary,
          setUsage,
          setRecentTranslations,
          onStreamError,
          onStage: updateFileStage,
        })
      }
      setIsTranslating(false)
      if (fileSucceeded) trackFirstTranslation({ signedIn: isAuthed })
      return
    }

  }, [
    tab,
    inputText,
    sourceLang,
    targetLang,
    targetLanguageName,
    gender,
    speakingTo,
    resolveEffectiveSpeakingTo,
    style,
    mode,
    model,
    charLimit,
    file,
    fileKind,
    fileBase64,
    pdfPageCount,
    isTranslating,
    isAuthed,
    isPro,
    openSignIn,
    openPaywall,
    updateFileStage,
    clearResult,
    maybeAutoCopy,
  ])

  useEffect(() => { runTranslateRef.current = runTranslate }, [runTranslate])

  const rerunForAudience = useCallback((audience: GenderId) => {
    const baseline = resultText.trim()
    if (!baseline) return

    // A signed-in gateway result has a server-owned row id. Bind the new
    // request to it so the Worker can recover a trusted continuity baseline.
    const lastGatewayResult = gatewayLastPushRef.current
    const lastApiResult = apiLastAudienceResultRef.current
    pendingAudienceChangeFromIdRef.current =
      lastGatewayResult?.translatedText === baseline
        ? lastGatewayResult.id
        : null
    pendingAudienceChangeTokenRef.current =
      lastApiResult?.translatedText === baseline
        ? lastApiResult.token
        : null
    pendingSpeakingToRef.current = audience
    setSpeakingTo(audience)
    void runTranslate()
  }, [resultText, runTranslate])

  useEffect(() => {
    if (tab !== 'text') return
    if (!inputText.trim()) return
    if (suppressAutoTranslateRef.current) {
      suppressAutoTranslateRef.current = false
      return
    }
    setRetryCount(0)
    // Clear the stale result as soon as the user types something different
    // from what produced it. Keeps the target pane honest — no orphaned
    // translation paired with unrelated input.
    if (!isTranslating && outputRef.current && inputText !== lastTranslatedInputRef.current) {
      setOutput('')
      setTransliteration('')
      setReverseOutput('')
      setError(null)
      setLimitReached(false)
      setWordPopover(null)
    }
  }, [inputText, tab, isTranslating])

  useEffect(() => {
    if (tab === 'text' && !inputText.trim()) {
      setOutput('')
      setTransliteration('')
      setError(null)
    }
  }, [inputText, tab])

  /**
   * The single upload path. It reads the file's own kind and routes it, so
   * there is nothing to choose before uploading: an image, a PDF, a .docx and
   * a .pptx all land here and all end up on the right backend.
   *
   * Upload is still the gate — guests sign in, signed-in free users see the
   * paywall for the kind they picked — and translate time gates again.
   */
  const handlePickFile = useCallback(async (picked: File | null) => {
    if (!picked) return
    const kind = detectFileKind(picked)
    if (!kind) {
      toast.error('Choose an image, a PDF, a Word (.docx) or a PowerPoint (.pptx) file')
      return
    }
    if (!isAuthed) {
      openSignIn('sidebar')
      return
    }
    if (!isPro) {
      openPaywall(kind === 'image' ? 'image' : 'document')
      return
    }
    if (picked.size > FILE_MAX_BYTES) {
      toast.error(`That file is larger than ${FILE_MAX_MB} MB`)
      return
    }
    try {
      const base64 = await fileToBase64(picked)
      setFile(picked)
      setFileKind(kind)
      setFileBase64(base64)
      // Cheap page count off the PDF header, for the picker's caption and the
      // 20-page cap the transport enforces.
      if (kind === 'pdf') {
        try {
          const head = atob(base64.slice(0, Math.min(base64.length, 400_000)))
          const matches = head.match(/\/Type\s*\/Page[^s]/g)
          setPdfPageCount(matches ? matches.length : undefined)
        } catch {
          setPdfPageCount(undefined)
        }
      } else {
        setPdfPageCount(undefined)
      }
      clearResult()
      setRestoredMedia(null)
    } catch {
      toast.error('Could not read that file')
    }
  }, [isAuthed, isPro, openSignIn, openPaywall, clearResult])

  const handlePasteFromClipboard = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.readText) {
      toast.error('Paste is not available in this browser')
      return
    }

    try {
      const text = await navigator.clipboard.readText()
      if (!text.trim()) {
        toast.error('Clipboard is empty')
        return
      }
      setTab('text')
      justPastedRef.current = true
      userEditRef.current = true
      setInputText(text)
      setError(null)
      setLimitReached(false)
    } catch {
      toast.error('Could not read from clipboard')
    }
  }, [])

  // The textarea's own onChange — thin wrapper so the prefetch effect can
  // tell a real keystroke apart from every OTHER `setInputText` call in this
  // file (restore, swap, clear, example fill, open-recent), none of which
  // are the user composing something new.
  const handleInputTextChange = useCallback((value: string) => {
    userEditRef.current = true
    setInputText(value)
  }, [])

  // Example chips shown on the empty translator state — fill the input and
  // translate immediately, same deferred-trigger pattern as Summarize (state
  // updates aren't visible to runTranslate's closure until the next tick).
  const handleExampleClick = useCallback((example: string) => {
    setInputText(example)
    setError(null)
    setLimitReached(false)
    setTimeout(() => { runTranslateRef.current() }, 0)
  }, [])

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items
      if (!items) return
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          const pasted = item.getAsFile()
          if (pasted) {
            setTab('file')
            handlePickFile(pasted)
            e.preventDefault()
            return
          }
        }
      }
      // No image on the clipboard — a text paste into the source textarea
      // falls through to the browser's default handling. Flag it so the
      // prefetch effect below (keyed on `inputText`) treats the resulting
      // change as a paste rather than timing it out as a pause, and counts
      // as the user composing (see `userEditRef`).
      if (tab === 'text') {
        justPastedRef.current = true
        userEditRef.current = true
      }
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [handlePickFile, tab])

  // Pre-translation prefetch: warms the gateway's cache for the request the
  // user is most likely to submit next, so the real translation (whenever
  // they hit Translate) can come back instantly instead of waiting on the
  // model. Paste fires right away; ordinary typing waits for a pause. Keyed
  // ONLY on `inputText` — every other value this needs (`sourceLang`,
  // `charLimit`, `tab`, `mode`, etc.) is read through `prefetchLiveRef`
  // instead of being a dependency, so a picker change alone can never
  // re-run this and cancel a keystroke's pending 800ms debounce; `sendPrefetch`
  // (whether it fires immediately or after the delay) always sees whatever
  // is live at that moment anyway. See `./translator/prefetchTrigger.ts` for
  // the pure decision rules and `prefetchViaGateway`'s doc comment for the
  // server contract.
  useEffect(() => {
    const previousText = prefetchPrevTextRef.current
    prefetchPrevTextRef.current = inputText
    const explicitPaste = justPastedRef.current
    justPastedRef.current = false
    const composing = userEditRef.current
    userEditRef.current = false

    if (prefetchPauseTimerRef.current) {
      clearTimeout(prefetchPauseTimerRef.current)
      prefetchPauseTimerRef.current = null
    }

    // `composing` is only true for a change the user actually typed or
    // pasted just now — opening a recent/history item, swapping languages
    // (moves the output into the input), clearing, an example-chip fill, or
    // any other programmatic `setInputText` never sets `userEditRef`, so
    // `classifyTextChange` below always returns null for them (see its own
    // doc comment). `prefetchPrevTextRef` is still updated above regardless,
    // so the next real keystroke measures its growth against the text
    // that's actually on screen, not against whatever was there before the
    // programmatic change.
    const trigger = classifyTextChange({ previousText, nextText: inputText, explicitPaste, composing })
    if (!trigger) return

    const sendPrefetch = () => {
      // A prefetch warms the GATEWAY's cache and is itself an LLM call. When
      // this tab's translations are not routed there, nothing would ever read
      // what it warmed; before the routing check has answered, ask it first.
      const routing = gatewaySourceStatus()
      if (routing === 'fallback') return
      if (routing === 'checking') {
        void ensureGatewaySourceProbe().then((next) => {
          if (next === 'gateway') sendPrefetch()
        })
        return
      }
      const live = prefetchLiveRef.current
      // Same normalization `runTranslate` uses for the real request's
      // `text` — see `normalizeTranslateText`'s doc comment.
      const normalizedText = normalizeTranslateText(inputText)
      if (
        shouldSkipPrefetch({
          trimmedTextLength: normalizedText.length,
          charLimit: live.charLimit,
          tab: live.tab,
          mode: live.mode,
          isTranslating: live.isTranslating,
        })
      ) {
        return
      }
      const params = {
        text: normalizedText,
        sourceLang: live.sourceLang,
        targetLang: live.targetLang,
        gender: live.gender,
        genderContext: resolveEffectiveSpeakingTo(),
        mode: live.mode,
        model: live.model,
        style: live.style || undefined,
      }
      const signature = prefetchSignature(params)
      if (signature === prefetchLastSigRef.current) return
      prefetchLastSigRef.current = signature
      void prefetchViaGateway(params, trigger)
    }

    if (trigger === 'paste') {
      sendPrefetch()
      return
    }
    prefetchPauseTimerRef.current = setTimeout(sendPrefetch, PREFETCH_PAUSE_DELAY_MS)
    return () => {
      if (prefetchPauseTimerRef.current) clearTimeout(prefetchPauseTimerRef.current)
    }
  }, [inputText, resolveEffectiveSpeakingTo])

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
      if (detectTimerRef.current) clearTimeout(detectTimerRef.current)
      if (copyFlagTimerRef.current) clearTimeout(copyFlagTimerRef.current)
      if (saveFlagTimerRef.current) clearTimeout(saveFlagTimerRef.current)
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current)
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.src = ''
      }
    }
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        runTranslate()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [runTranslate])

  useEffect(() => {
    if (output && !isTranslating && targetLang === 'he' && speakingTo === 'general' && output.length < 500) {
      setShowGenderNudge(true)
    } else {
      setShowGenderNudge(false)
    }
  }, [output, isTranslating, targetLang, speakingTo])

  const handleSwap = useCallback(() => {
    setSourceLang(targetLang)
    setTargetLang(sourceLang)
    // On the File tab the only thing to swap is the direction: the picked
    // image or PDF stays the source. Copying the hidden textarea draft into
    // `output` here is what appended the last typed text under an image card.
    if (tab !== 'text') return
    setInputText(output)
    setOutput(inputText)
    setTransliteration('')
    setReverseOutput('')
    setVariations([])
    setMediaResult(null)
    // The swapped pair is a valid input/output pairing. Suppress the
    // input-change effect so it doesn't wipe the output, and record the
    // new pairing so later edits clear correctly.
    suppressAutoTranslateRef.current = true
    lastTranslatedInputRef.current = output
  }, [tab, sourceLang, targetLang, output, inputText])

  const handleSpeak = useCallback(async () => {
    if (!resultText || typeof window === 'undefined') return
    // A second click while the clip is still being made is a cancel of the
    // wait, not a second request.
    if (isPreparingSpeech) {
      speakRunRef.current += 1
      setIsPreparingSpeech(false)
      return
    }

    if (isSpeaking && audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      setIsSpeaking(false)
      return
    }

    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current)
      audioUrlRef.current = null
    }

    // Which voice speaks is `speakWithChoice`'s call, shared with every other
    // Listen button: the on-device voice first when it is picked in Settings
    // or there is no connection, otherwise ElevenLabs, with the downloaded
    // voice standing in for any refusal or failure. The sign-in and Pro checks
    // live inside `cloud` so a free user who downloaded the voice hears it
    // rather than meeting the paywall.
    const speakRun = ++speakRunRef.current
    setIsPreparingSpeech(true)
    try {
      const { audioUrl } = await offlineVoice.speakWithChoice({
        text: resultText,
        language: targetLang,
        gender: voiceGender,
        cloud: () => {
          if (!isAuthed) throw Object.assign(new Error('Sign in to listen'), { type: 'auth_required' })
          if (!isPro) throw Object.assign(new Error('Voice is a Pro feature'), { type: 'pro_required' })
          return generateSpeech({ text: resultText, voiceGender, lang: targetLang })
        },
      })
      // Cancelled while the clip was being made: ElevenLabs caches it server
      // side under the same id, so nothing is wasted by dropping it here.
      if (speakRun !== speakRunRef.current) {
        URL.revokeObjectURL(audioUrl)
        return
      }
      audioUrlRef.current = audioUrl
      const audio = new Audio(audioUrl)
      audioRef.current = audio
      audio.onended = () => setIsSpeaking(false)
      audio.onerror = () => {
        setIsSpeaking(false)
        toast.error('Could not play audio')
      }
      setIsPreparingSpeech(false)
      setIsSpeaking(true)
      await audio.play()
    } catch (err) {
      const e = err as Error & { type?: string }
      setIsPreparingSpeech(false)
      setIsSpeaking(false)
      if (speakRun !== speakRunRef.current) return
      if (e.type === 'auth_required') {
        setIsAuthed(false)
        openSignIn('sidebar')
        return
      }
      if (e.type === 'pro_required') {
        openPaywall('voice')
        return
      }
      if (e.type === 'limit_reached') {
        toast.error(e.message || 'Monthly voice limit reached')
        return
      }
      toast.error('Could not generate audio. Please try again.')
    }
  }, [resultText, targetLang, voiceGender, isSpeaking, isPreparingSpeech, isAuthed, isPro, openSignIn, openPaywall])

  const handleGenerateVariations = useCallback(async () => {
    if (!output || isTranslating) return
    if (!isAuthed) {
      openSignIn('sidebar')
      return
    }
    if (!isPro) {
      openPaywall('word-alternatives')
      return
    }
    if (output.length > 150) return
    setVariationsLoading(true)
    setVariations([])
    try {
      const result = await fetchTranslationVariations({
        translation: output,
        sourceText: inputText.trim(),
        sourceLang,
        targetLang,
      })
      if (result.proRequired) {
        openPaywall('word-alternatives')
        return
      }
      setVariations(result.variations)
      if (result.variations.length === 0) {
        toast.error('No variations right now. Try again in a moment.')
      }
    } catch {
      toast.error('Could not generate variations')
    } finally {
      setVariationsLoading(false)
    }
  }, [output, inputText, sourceLang, targetLang, isAuthed, isPro, isTranslating, openPaywall, openSignIn])

  // Clear variations whenever the underlying translation changes.
  useEffect(() => {
    setVariations([])
    setVariationsLoading(false)
  }, [output])

  const handleFavorite = useCallback(async () => {
    if (!isSignedIn()) {
      openSignIn('library')
      return
    }
    const translated = output.trim()
    if (!translated) return

    // The ledger already holds this translation. Name its row when we can
    // (the id from this turn's push) and let the library layer find it by
    // text when we can't — writing a new row is what used to make a saved
    // translation reappear in History as a fresh one.
    const lastPush = gatewayLastPushRef.current
    const knownId = lastPush && lastPush.translatedText === translated ? lastPush.id : null
    const rowParams = {
      knownId,
      sourceText: inputText.trim(),
      translatedText: output,
      sourceLang,
      targetLang,
      mode,
      model,
      gender,
    }

    // Second click takes it back off. A bookmark that only ever fills is a
    // trap: the way out was a trip to the Library.
    if (savedOutput && savedOutput === translated) {
      setSavedOutput('')
      try {
        const { outcome } = await unfavoriteGatewayTranslation(rowParams)
        if (outcome === 'unavailable') {
          // No ledger row — the web's own favorites table holds it, and that
          // row is addressed by its own id, so find it by what it says.
          const rows = await dispatch(babaApi.endpoints.libraryFavorites.initiate(undefined)).unwrap()
          const match = rows.find((row) => (row.translated_text ?? '').trim() === translated)
          if (match) await dispatch(babaApi.endpoints.removeFavorite.initiate(match.id)).unwrap()
        }
        toast.success('Removed from Library')
      } catch {
        setSavedOutput(translated)
        toast.error('Could not remove it from Library')
      }
      return
    }

    // Fill the bookmark on the click, not after the round trip. A failure
    // below puts it back, which is the only case anyone waits for.
    setSavedOutput(translated)

    try {
      const { outcome, id } = await favoriteGatewayTranslation(rowParams)
      const saveToWebFavorites = () =>
        dispatch(
          babaApi.endpoints.saveFavorite.initiate({
            source_text: inputText.trim(),
            translated_text: output,
            source_lang: sourceLang,
            target_lang: targetLang,
            gender,
          }),
        ).unwrap()

      if (outcome === 'unavailable') {
        // No gateway ledger for this session — the web's own favorites table
        // is the source of truth, exactly as before.
        await saveToWebFavorites()
      } else {
        if (id) gatewayLastPushRef.current = { id, translatedText: translated }
        // The browser extension reads its saved list from apps/api's own
        // favorites table, not from the ledger, so mirror it there too —
        // best effort, since the ledger write above already succeeded.
        void saveToWebFavorites().catch(() => {})
      }
      toast.success('Saved to Library')
    } catch {
      setSavedOutput('')
      toast.error('Could not save to Library')
    }
  }, [dispatch, gender, inputText, mode, model, openSignIn, output, savedOutput, sourceLang, targetLang])

  const handleReverseTranslate = useCallback(async () => {
    if (!output.trim() || isReversing) return
    setIsReversing(true)
    setReverseOutput('')
    const run = reverseRunRef.current
    let acc = ''
    await translateStream({
      text: output,
      sourceLang: targetLang,
      targetLang: sourceLang,
      gender: 'general',
      mode: 'standard',
      model: 'balanced',
      onToken: (chunk) => {
        if (reverseRunRef.current !== run) return
        acc += chunk
        setReverseOutput(acc)
      },
      onError: () => { if (reverseRunRef.current === run) toast.error("Couldn't reverse-translate") },
      onDone: () => { if (reverseRunRef.current === run) setIsReversing(false) },
    })
    if (reverseRunRef.current === run) setIsReversing(false)
  }, [output, sourceLang, targetLang, isReversing])

  const handleToggleAutoCopy = useCallback((next: boolean) => {
    autoCopyRef.current = next
    setAutoCopy(next)
    savePrefs({ autoCopy: next })
    toast.success(next ? 'Auto-copy on' : 'Auto-copy off')
  }, [])

  const handleCopyFormatted = useCallback(async (variant: 'plain' | 'with-translit' | 'markdown') => {
    if (!resultText) return
    let text = resultText
    if (variant === 'with-translit' && transliteration) {
      text = `${resultText}\n\n${transliteration}`
    } else if (variant === 'markdown') {
      const sourceName = LANGUAGES.find((l) => l.code === sourceLang)?.name
      const targetName = LANGUAGES.find((l) => l.code === targetLang)?.name
      text = `**${sourceName} → ${targetName}**\n\n> ${inputText.trim()}\n\n${resultText}${transliteration ? `\n\n_${transliteration}_` : ''}`
    }
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      if (copyFlagTimerRef.current) clearTimeout(copyFlagTimerRef.current)
      copyFlagTimerRef.current = setTimeout(() => setCopied(false), 1400)
      toast.success('Copied')
      setShowCopyMenu(false)
    } catch { toast.error('Copy failed') }
  }, [resultText, transliteration, inputText, sourceLang, targetLang])

  const handleShareLink = useCallback(async () => {
    if (!output) return
    const payload = {
      s: inputText.trim(),
      t: resultText,
      sl: sourceLang,
      tl: targetLang,
    }
    try {
      const encoded = btoa(encodeURIComponent(JSON.stringify(payload)))
      const url = `${window.location.origin}/t#${encoded}`
      await navigator.clipboard.writeText(url)
      toast.success('Share link copied')
    } catch { toast.error('Share failed') }
  }, [inputText, resultText, sourceLang, targetLang])

  const handleClear = useCallback(() => {
    setActiveRecentId(null)
    abortRef.current?.abort()
    setIsTranslating(false)
    setInputText('')
    clearResult()
    setFile(null)
    setFileKind(null)
    setFileBase64('')
    setPdfPageCount(undefined)
    setTab('text')
    setRestoredMedia(null)
    try { localStorage.removeItem(OUTPUT_KEY) } catch {}
  }, [clearResult])

  // Summarize the finished translation inline. Re-runs the same input in
  // summary mode, replaces the output, then drops back to standard mode so
  // the next translation is a normal one.
  const handleSummarizeText = useCallback(() => {
    if (!output || isTranslating) return
    if (!isAuthed) {
      openSignIn('sidebar')
      return
    }
    if (!isPro) {
      openPaywall('summary-mode')
      return
    }
    setMode('summary')
    setOutput('')
    setIsSummarizing(true)
    setTimeout(async () => {
      await runTranslateRef.current()
      setMode('standard')
    }, 0)
  }, [output, isTranslating, isAuthed, isPro, openSignIn, openPaywall])

  const handleNewTranslation = useCallback(() => {
    handleClear()
    setRetryCount(0)
  }, [handleClear])

  useEffect(() => {
    const onNew = () => handleNewTranslation()
    const onOpenRecent = (e: Event) => {
      const detail = (e as CustomEvent<RecentTranslation>).detail
      if (!detail) return
      suppressAutoTranslateRef.current = true
      clearResult()
      setRestoredMedia(null)
      setTab('text')
      setInputText(detail.inputText)
      setOutput(detail.output)
      lastTranslatedInputRef.current = detail.inputText
      setSourceLang(detail.sourceLang)
      setTargetLang(detail.targetLang)
    }
    window.addEventListener('baba:new-translation', onNew)
    window.addEventListener('baba:open-recent', onOpenRecent as EventListener)
    return () => {
      window.removeEventListener('baba:new-translation', onNew)
      window.removeEventListener('baba:open-recent', onOpenRecent as EventListener)
    }
  }, [handleNewTranslation, clearResult])

  // Shared cancel — used by the toolbar's Stop button, the mobile bottom bar,
  // the target pane's inline Cancel, and the source pane's file-job Cancel.
  const cancelTranslation = useCallback(() => {
    abortRef.current?.abort()
    setIsTranslating(false)
  }, [])

  // Toolbar/source-pane "Attach" — a single control that routes to whichever
  // existing gated handler matches the picked file's type.
  const handleAttachFile = useCallback((attached: File | null) => {
    if (!attached) return
    setTab('file')
    handlePickFile(attached)
  }, [handlePickFile])

  const handleCopyTransliterationOnly = useCallback(async () => {
    if (!transliteration) return
    try {
      await navigator.clipboard.writeText(transliteration)
      toast.success('Copied')
    } catch {
      toast.error('Copy failed')
    }
  }, [transliteration])

  // Global command-palette + workspace shortcuts (⌘Enter is handled by its
  // own effect above). Modifier-only combos, so they never collide with
  // normal typing; skip while another modal already owns the keyboard.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return
      if (showReverseIntro) return
      const key = e.key.toLowerCase()
      if (key === 'k') {
        e.preventDefault()
        setCommandPaletteOpen((v) => !v)
        return
      }
      if (commandPaletteOpen) return
      if (key === 'n') { e.preventDefault(); handleNewTranslation(); return }
      if (key === 'l') { e.preventDefault(); handleSpeak(); return }
      if (key === 's' && e.shiftKey) { e.preventDefault(); handleSwap(); return }
      if (key === 's') { e.preventDefault(); handleFavorite(); return }
      if (key === 'c' && e.shiftKey) { e.preventDefault(); handleCopyFormatted('plain'); return }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [showReverseIntro, commandPaletteOpen, handleNewTranslation, handleSpeak, handleSwap, handleFavorite, handleCopyFormatted])

  const overCharLimit = tab === 'text' && !isPro && inputText.length > charLimit
  const canTranslate =
    !isTranslating &&
    !overCharLimit &&
    ((tab === 'text' && inputText.trim().length > 0) || (tab === 'file' && !!fileBase64))
  const mobileShowOutput = isTranslating || output.length > 0 || !!error

  /**
   * Restores a TEXT row from the History panel. Media rows never arrive here
   * — the page opens those in its read-only result surface — and every field
   * is coerced anyway, because a ledger row can carry a null source text and
   * every downstream read of inputText/output assumes a string.
   */
  const handleSelectHistoryRow = (row: HistoryRow) => {
    abortRef.current?.abort()
    setIsTranslating(false)
    setActiveRecentId(row.id)
    suppressAutoTranslateRef.current = true
    // Whatever was on screen — an image card, a reverse check, a
    // pronunciation line — belongs to the previous translation, not to the
    // row being restored.
    clearResult()
    setRestoredMedia(null)
    setTab('text')
    setInputText(row.inputText ?? '')
    setOutput(row.output ?? '')
    lastTranslatedInputRef.current = row.inputText ?? ''
    // `as LangCode` used to be a lie here: 291 of the 416 rows on this account
    // store a display name ('English', 'Hebrew', 'French') rather than a code,
    // and setting one crashed the panes on the next render.
    setSourceLang(toLangCode(row.sourceLang, DEFAULT_SOURCE))
    setTargetLang(toLangCode(row.targetLang, DEFAULT_TARGET))
  }
  /**
   * An image/PDF history row lands in the page like a fresh result: the
   * original preview on the left, the structured translation on the right.
   * It used to open a modal, which is the one thing History must never do.
   */
  const handleOpenHistoryMedia = (media: HistoryRowMedia) => {
    abortRef.current?.abort()
    setIsTranslating(false)
    setActiveRecentId(media.id)
    suppressAutoTranslateRef.current = true
    setFile(null); setFileKind(null); setFileBase64(''); setPdfPageCount(undefined)
    setInputText('')
    clearResult()
    setSourceLang(toLangCode(media.source_lang, DEFAULT_SOURCE))
    setTargetLang(toLangCode(media.target_lang, DEFAULT_TARGET))
    const kind = media.content_type === 'image' ? 'image' : media.content_type === 'document' ? 'document' : 'pdf'
    setRestoredMedia({ kind, imageUrl: media.image_url, label: kind === 'image' ? 'Image Translation' : kind === 'pdf' ? 'PDF Translation' : 'Document Translation' })
    setMediaResult(parseMediaResult(media.translated_text))
    setTab('file')
  }
  // Phone layout: the result card only exists once there is something to put
  // in it. Until then the input card has the whole pane and the examples.
  const mobileShowTarget = isTranslating || !!output || !!error || limitReached || !!limitKind || !!mediaResult || !!reverseOutput || tab === 'file'

  const handleClearRecent = () => {
    setRecentTranslations([])
    try { localStorage.removeItem(RECENT_TRANSLATIONS_KEY) } catch {}
  }

  return (
    // data-private: PostHog replay masks every character inside (see
    // maskTextSelector in lib/posthog.ts); what people translate stays theirs.
    <div className="h-full w-full min-w-0 flex overflow-hidden" data-private>
      <ContextPanel>
        <HistoryPanel
          recents={recentTranslations}
          activeId={activeRecentId}
          onSelectText={handleSelectHistoryRow}
          onOpenMedia={handleOpenHistoryMedia}
          onClearLocal={handleClearRecent}
        />
      </ContextPanel>

      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        <Toolbar
          sourceLang={sourceLang}
          targetLang={targetLang}
          onSourceLangChange={setSourceLangSafe}
          onTargetLangChange={setTargetLangSafe}
          onSwap={handleSwap}
          onOpenCommands={() => setCommandPaletteOpen(true)}
        />

        <MobilePageHeader title="Translate" onOpenCommands={() => setCommandPaletteOpen(true)} />

        <MobileHeader
          sourceLang={sourceLang}
          targetLang={targetLang}
          onSourceLangChange={setSourceLangSafe}
          onTargetLangChange={setTargetLangSafe}
          onSwap={handleSwap}
          onOpenHistory={() => setMobileHistoryOpen(true)}
        />

        {/*
         * Phone: one column of cards. The input card takes the whole pane
         * until there is something to show under it (a translation in
         * flight, output, a file, an error), then it shrinks to a header
         * and the result card takes the room. Desktop keeps the two panes
         * side by side; none of the max-md classes reach it.
         */}
        <div className={cn('flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden max-md:px-4 max-md:pt-3 max-md:gap-3', mobileShowTarget && 'max-md:overflow-y-auto max-md:pb-3')}>
          <div
            className={cn(
              'shrink-0 md:h-auto md:flex-1 md:min-w-0 overflow-hidden flex flex-col',
              'max-md:rounded-md max-md:border max-md:border-app max-md:shadow-token-sm',
              mobileShowTarget ? 'max-md:max-h-[30%] max-md:shrink-0 max-md:[&_.ws-source-footer]:hidden' : 'max-md:flex-1 max-md:min-h-0',
            )}
          >
            <TranslatorSourcePane
              tab={tab}
              sourceLang={sourceLang}
              sourceRtl={!!sourceMeta?.rtl}
              inputText={inputText}
              onInputText={handleInputTextChange}
              charLimit={charLimit}
              isTranslating={isTranslating}
              onCancelFileJob={cancelTranslation}
              onClear={handleClear}
              onPasteFromClipboard={handlePasteFromClipboard}
              onAttach={handleAttachFile}
              canTranslate={canTranslate}
              hasCompletedTranslation={hasCompletedTranslation}
              onCancelTranslate={cancelTranslation}
              onExampleClick={handleExampleClick}
              runTranslate={runTranslate}
              file={file}
              filePreviewUrl={filePreviewUrl}
              pdfPageCount={pdfPageCount}
              restoredMedia={restoredMedia}
              isAuthed={isAuthed}
              isPro={isPro}
              onLimitAction={() => (isAuthed ? openPaywall('text-too-long') : openSignIn('upgrade-free'))}
              fileJob={fileJob}
              translationStartMs={translationStartMs}
              hebrewFacts={HEBREW_FACTS}
              factIndex={factIndex}
            />
          </div>

          <div
            className={cn(
              'flex-1 min-h-0 md:min-w-0 overflow-hidden flex-col',
              'max-md:rounded-md max-md:border max-md:shadow-token-sm max-md:flex-none max-md:overflow-visible',
              mobileShowTarget ? 'flex max-md:border-app' : 'hidden md:flex',
            )}
          >
            <TranslatorTargetPane
              targetLang={targetLang}
              sourceLang={sourceLang}
              mode={mode}
              tab={tab}
              isTranslating={isTranslating}
              output={output}
              mediaResult={mediaResult}
              summary={summary}
              originalText={originalText}
              sourceText={inputText.trim()}
              sourceRtl={!!sourceMeta?.rtl}
              isPro={isPro}
              transliteration={transliteration}
              showGenderNudge={showGenderNudge}
              onDismissGenderNudge={() => setShowGenderNudge(false)}
              onGenderWoman={() => rerunForAudience('to_female')}
              onGenderMan={() => rerunForAudience('to_male')}
              error={error}
              limitReached={limitReached}
              limitKind={limitKind}
              onUpgradeClick={() => (isAuthed ? openPaywall(limitKind === 'length' ? 'text-too-long' : 'limit-reached') : openSignIn('upgrade-free'))}
              onRetry={() => runTranslate()}
              reverseOutput={reverseOutput}
              isReversing={isReversing}
              onClearReverse={() => setReverseOutput('')}
              copied={copied}
              saved={savedOutput === output.trim() && !!savedOutput}
              showCopyMenu={showCopyMenu}
              setShowCopyMenu={setShowCopyMenu}
              onCopyFormatted={handleCopyFormatted}
              isSpeaking={isSpeaking}
              isPreparingSpeech={isPreparingSpeech}
              autoCopy={autoCopy}
              onToggleAutoCopy={handleToggleAutoCopy}
              onSpeak={handleSpeak}
              isProLoaded={isProLoaded}
              isAuthed={isAuthed}
              onFavorite={handleFavorite}
              onReverseTranslate={() => {
                if (!localStorage.getItem(REVERSE_INTRO_KEY)) {
                  setShowReverseIntro(true)
                  return
                }
                handleReverseTranslate()
              }}
              onShareLink={handleShareLink}
              onSummarizeText={handleSummarizeText}
              canSummarizeText={inputText.trim().length >= 200}
              retryCount={retryCount}
              onRetryWithContext={() => {
                setRetryCount((c) => c + 1)
                runTranslate(lastOutputRef.current || output)
              }}
              onCancel={cancelTranslation}
              usage={usage}
              isSummarizing={isSummarizing}
              translationElapsedMs={translationElapsedMs}
              variations={variations}
              variationsLoading={variationsLoading}
              onGenerateVariations={handleGenerateVariations}
              onPickVariation={(v) => {
                setOutput(v)
                setVariations([])
                toast.success('Swapped in alternative')
              }}
              speakingTo={speakingTo}
              selfGender={gender}
            />
          </div>
        </div>

        {isProLoaded && (
          <MobileUsageStrip
            isAuthed={isAuthed}
            isPro={isPro}
            onAction={() => (isAuthed ? openPaywall('upgrade') : openSignIn('upgrade-free'))}
          />
        )}

        <MobileBottomBar
          isTranslating={isTranslating}
          canTranslate={canTranslate}
          hasCompletedTranslation={hasCompletedTranslation}
          onTranslate={() => runTranslate()}
          onCancel={cancelTranslation}
          onNewTranslation={handleNewTranslation}
        />

        <StatusBar modelLabel="smart model" />
      </div>

      <MobileHistorySheet
        open={mobileHistoryOpen}
        onClose={() => setMobileHistoryOpen(false)}
        recents={recentTranslations}
        activeId={activeRecentId}
        onSelectText={handleSelectHistoryRow}
        onOpenMedia={handleOpenHistoryMedia}
        onClearLocal={handleClearRecent}
      />

      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        onNewTranslation={handleNewTranslation}
        onTranslate={() => runTranslate()}
        onSpeak={handleSpeak}
        onCopyTranslation={() => handleCopyFormatted('plain')}
        onCopyTransliteration={handleCopyTransliterationOnly}
        onSwap={handleSwap}
        onSave={handleFavorite}
        onSetSpeakingTo={setSpeakingTo}
      />

      {/* Reverse check intro modal */}
      {showReverseIntro && (
        <div className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in backdrop-blur-sm" style={{ background: 'var(--overlay)' }}>
          <div className="w-[340px] mx-4 rounded-3xl border border-app bg-surface p-6 shadow-token-lg animate-scale-in">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[15px] font-bold text-app">Reverse Check</span>
            </div>
            <p className="text-[13px] text-muted leading-relaxed mb-5">
              Reverse check translates your result back to the original language so you can verify the meaning was preserved accurately.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  localStorage.setItem(REVERSE_INTRO_KEY, '1')
                  setShowReverseIntro(false)
                  handleReverseTranslate()
                }}
                className="flex-1 h-10 rounded-full border border-app bg-surface-2 text-[13px] font-semibold text-muted hover:text-app hover:bg-[var(--card-2)] active:scale-95 transition-all cursor-pointer"
              >
                Do not show again
              </button>
              <button
                type="button"
                onClick={() => {
                  localStorage.setItem(REVERSE_INTRO_KEY, '1')
                  setShowReverseIntro(false)
                  handleReverseTranslate()
                }}
                className="flex-1 h-10 rounded-full bg-primary text-[13px] font-bold hover:bg-primary-hover active:scale-95 shadow-sm transition-all cursor-pointer"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {wordPopover && (
        <WordAlternativesPopover
          word={wordPopover.word}
          x={wordPopover.x}
          y={wordPopover.y}
          alternatives={wordPopover.alternatives}
          loading={wordPopover.loading}
          onClose={() => setWordPopover(null)}
          onPickAlternative={(alt) => {
            setOutput((prev) => prev.replace(new RegExp(wordPopover.word, 'gi'), alt))
            setWordPopover(null)
          }}
        />
      )}
    </div>
  )
}
